import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { apiClient, errorDetail } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { EditWithHistory } from '@/components/catalog/EditWithHistory';

interface RequiredResource {
  resourceId: string;
  name: string;
  type: string;
  mandatory: boolean;
  setupUrl?: string | null;
  notes?: string | null;
}

type PricingModel = 'hourly' | 'fixed' | 'retainer' | 'markup';

interface ServiceItem {
  id: string;
  serviceId: string;
  name: string;
  category: string;
  description: string | null;
  pricingModel: PricingModel;
  defaultProfile: string | null;
  estimatedHours: number | null;
  fixedPriceUsd: number | null;
  fixedCostUsd: number | null;
  retainerHoursPerMonth: number | null;
  retainerPriceMrrUsd: number | null;
  retainerCostMrrUsd: number | null;
  markupPct: number | null;
  upstreamProvider: string | null;
  upstreamCostUnitUsd: number | null;
  unitLabel: string | null;
  countries: string[];
  tags: string[];
  requiredResources: RequiredResource[];
  notes: string | null;
  status: 'ACTIVE' | 'DRAFT' | 'ARCHIVED';
}

interface UpsertPayload {
  serviceId: string;
  name: string;
  category: string;
  description: string | null;
  pricingModel: PricingModel;
  defaultProfile: string | null;
  estimatedHours: number | null;
  fixedPriceUsd: number | null;
  fixedCostUsd: number | null;
  retainerHoursPerMonth: number | null;
  retainerPriceMrrUsd: number | null;
  retainerCostMrrUsd: number | null;
  markupPct: number | null;
  upstreamProvider: string | null;
  upstreamCostUnitUsd: number | null;
  unitLabel: string | null;
  countries: string[];
  tags: string[];
  requiredResources: RequiredResource[];
  notes: string | null;
  status: 'ACTIVE' | 'DRAFT' | 'ARCHIVED';
}

interface PagedResult<T> { items: T[]; total: number; limit: number; offset: number; }
interface Facets {
  categories: string[]; countries: string[]; tags: string[];
  statuses: string[]; pricingModels: string[];
}

const EMPTY: UpsertPayload = {
  serviceId: '', name: '', category: 'consulting',
  description: null,
  pricingModel: 'fixed',
  defaultProfile: null, estimatedHours: null,
  fixedPriceUsd: null, fixedCostUsd: null,
  retainerHoursPerMonth: null, retainerPriceMrrUsd: null, retainerCostMrrUsd: null,
  markupPct: null, upstreamProvider: null, upstreamCostUnitUsd: null, unitLabel: null,
  countries: [], tags: [], requiredResources: [],
  notes: null, status: 'ACTIVE',
};

const PAGE_SIZE = 25;

function usd(n: number | null | undefined): string {
  if (n == null) return '—';
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(n);
}

function marginPct(price: number | null, cost: number | null): string {
  if (!price || price <= 0) return '—';
  if (cost == null) return '—';
  return `${(((price - cost) / price) * 100).toFixed(0)}%`;
}

export function ServicesCatalogPage() {
  const [items, setItems] = useState<ServiceItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<ServiceItem | null>(null);
  const [creating, setCreating] = useState(false);

  const [search, setSearch] = useState('');
  const [country, setCountry] = useState('');
  const [category, setCategory] = useState('');
  const [pricingModel, setPricingModel] = useState('');
  const [statusFilter, setStatusFilter] = useState('ACTIVE');
  const [tag, setTag] = useState('');
  const [page, setPage] = useState(0);

  const [facets, setFacets] = useState<Facets>({
    categories: [], countries: [], tags: [],
    statuses: ['ACTIVE', 'DRAFT', 'ARCHIVED'],
    pricingModels: ['hourly', 'fixed', 'retainer', 'markup'],
  });

  useEffect(() => {
    apiClient.get<Facets>('/admin/services-catalog/facets')
      .then(({ data }) => setFacets((prev) => ({ ...prev, ...data })))
      .catch(() => {});
  }, []);

  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (country) params.set('country', country);
    if (category) params.set('category', category);
    if (pricingModel) params.set('pricing_model', pricingModel);
    if (statusFilter) params.set('status', statusFilter);
    if (tag) params.set('tag', tag);
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(page * PAGE_SIZE));
    apiClient.get<PagedResult<ServiceItem>>(`/admin/services-catalog/search?${params}`)
      .then(({ data }) => { setItems(data.items); setTotal(data.total); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [debouncedSearch, country, category, pricingModel, statusFilter, tag, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [debouncedSearch, country, category, pricingModel, statusFilter, tag]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const clearFilters = () => {
    setSearch(''); setCountry(''); setCategory(''); setPricingModel('');
    setStatusFilter('ACTIVE'); setTag('');
  };
  const hasActiveFilters = Boolean(search || country || category || pricingModel || tag || statusFilter !== 'ACTIVE');

  const handleArchive = (id: string) => {
    if (!confirm('¿Archivar este servicio?')) return;
    apiClient.delete(`/admin/services-catalog/${id}`).then(() => load()).catch(() => {});
  };

  // helper to render sticker price based on pricing_model
  const stickerPrice = (s: ServiceItem): string => {
    switch (s.pricingModel) {
      case 'hourly':
        if (s.estimatedHours && s.defaultProfile) {
          return `~${s.estimatedHours}h · ${s.defaultProfile}`;
        }
        return 'hourly';
      case 'fixed':
        return usd(s.fixedPriceUsd);
      case 'retainer':
        return `${usd(s.retainerPriceMrrUsd)}/mo · ${s.retainerHoursPerMonth}h`;
      case 'markup':
        return `${usd(s.upstreamCostUnitUsd)} + ${s.markupPct}% / ${s.unitLabel}`;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Services Catalog</h1>
          <p className="text-sm text-muted-foreground">
            Catálogo Lopettia de servicios profesionales · consulting · implementation · training · support · resold
          </p>
        </div>
        <Button size="sm" onClick={() => setCreating(true)}>+ Servicio</Button>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-card p-3">
        <Input placeholder="🔎 buscar…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-64" />
        <select className="rounded border bg-background px-2 py-1.5 text-sm" value={country} onChange={(e) => setCountry(e.target.value)}>
          <option value="">país (todos)</option>
          {facets.countries.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="rounded border bg-background px-2 py-1.5 text-sm" value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">categoría (todas)</option>
          {facets.categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="rounded border bg-background px-2 py-1.5 text-sm" value={pricingModel} onChange={(e) => setPricingModel(e.target.value)}>
          <option value="">pricing (todos)</option>
          {facets.pricingModels.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select className="rounded border bg-background px-2 py-1.5 text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">status (todos)</option>
          {facets.statuses.map((s) => <option key={s} value={s}>{s.toLowerCase()}</option>)}
        </select>
        {facets.tags.length > 0 && (
          <select className="rounded border bg-background px-2 py-1.5 text-sm" value={tag} onChange={(e) => setTag(e.target.value)}>
            <option value="">tag (todos)</option>
            {facets.tags.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
        {hasActiveFilters && <Button size="sm" variant="ghost" onClick={clearFilters}>limpiar</Button>}
        <div className="ml-auto text-xs text-muted-foreground">
          {total} {total === 1 ? 'resultado' : 'resultados'}
          {totalPages > 1 && ` · página ${page + 1} de ${totalPages}`}
        </div>
      </div>

      {creating && (
        <ServiceForm initial={EMPTY} onCancel={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />
      )}

      {loading && <p className="text-sm text-muted-foreground">cargando…</p>}

      <div className="overflow-hidden rounded-lg border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-2 text-left">servicio</th>
              <th className="px-4 py-2 text-left">categoría</th>
              <th className="px-4 py-2 text-left">países</th>
              <th className="px-4 py-2 text-left">pricing</th>
              <th className="px-4 py-2 text-right">precio</th>
              <th className="px-4 py-2 text-right">margen</th>
              <th className="px-4 py-2 text-left">recursos</th>
              <th className="px-4 py-2 text-left">status</th>
              <th className="px-4 py-2 text-right">acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {items.map((s) => (
              <>
                <tr key={s.id} className={s.status === 'ARCHIVED' ? 'opacity-50' : ''}>
                  <td className="px-4 py-2">
                    <div className="font-medium">{s.name}</div>
                    <div className="font-mono text-xs text-muted-foreground">{s.serviceId}</div>
                  </td>
                  <td className="px-4 py-2"><span className="rounded bg-muted px-2 py-0.5 text-xs">{s.category}</span></td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-1">
                      {s.countries.length === 0 ? (
                        <span className="text-xs text-muted-foreground">global</span>
                      ) : s.countries.map((c) => (
                        <span key={c} className="rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-900 dark:bg-blue-900/30 dark:text-blue-200">{c}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2"><span className="rounded bg-purple-100 px-2 py-0.5 text-xs text-purple-900 dark:bg-purple-900/30 dark:text-purple-200">{s.pricingModel}</span></td>
                  <td className="px-4 py-2 text-right font-mono text-xs">{stickerPrice(s)}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs">
                    {s.pricingModel === 'fixed' ? marginPct(s.fixedPriceUsd, s.fixedCostUsd)
                     : s.pricingModel === 'retainer' ? marginPct(s.retainerPriceMrrUsd, s.retainerCostMrrUsd)
                     : '—'}
                  </td>
                  <td className="px-4 py-2">
                    {s.requiredResources.length === 0 ? (
                      <span className="text-xs text-muted-foreground">—</span>
                    ) : (
                      <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-900 dark:bg-amber-900/30 dark:text-amber-200"
                        title={s.requiredResources.map((r) => `${r.name} (${r.type})${r.mandatory ? ' *' : ''}`).join('\n')}>
                        {s.requiredResources.length}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <span className={s.status === 'ACTIVE' ? 'text-xs text-green-600' : s.status === 'DRAFT' ? 'text-xs text-yellow-600' : 'text-xs text-muted-foreground'}>
                      {s.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => setEditing(s)} className="text-xs text-primary hover:underline">editar</button>
                    {s.status === 'ACTIVE' && (
                      <button onClick={() => handleArchive(s.id)} className="ml-3 text-xs text-muted-foreground hover:underline">archivar</button>
                    )}
                  </td>
                </tr>
                {editing?.id === s.id && (
                  <tr>
                    <td colSpan={9} className="bg-muted/20 px-4 py-4">
                      <EditWithHistory entityType="service" entityId={s.id} editForm={<ServiceForm initial={toPayload(s)} editingId={s.id} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />} />
                    </td>
                  </tr>
                )}
              </>
            ))}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  {hasActiveFilters ? 'sin resultados con esos filtros' : 'sin servicios · click "+ Servicio"'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between rounded-lg border bg-card px-4 py-2 text-sm">
          <div className="text-xs text-muted-foreground">mostrando {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} de {total}</div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>← anterior</Button>
            <Button size="sm" variant="outline" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>siguiente →</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function toPayload(s: ServiceItem): UpsertPayload {
  return {
    serviceId: s.serviceId, name: s.name, category: s.category, description: s.description,
    pricingModel: s.pricingModel, defaultProfile: s.defaultProfile, estimatedHours: s.estimatedHours,
    fixedPriceUsd: s.fixedPriceUsd, fixedCostUsd: s.fixedCostUsd,
    retainerHoursPerMonth: s.retainerHoursPerMonth, retainerPriceMrrUsd: s.retainerPriceMrrUsd, retainerCostMrrUsd: s.retainerCostMrrUsd,
    markupPct: s.markupPct, upstreamProvider: s.upstreamProvider, upstreamCostUnitUsd: s.upstreamCostUnitUsd, unitLabel: s.unitLabel,
    countries: s.countries, tags: s.tags, requiredResources: s.requiredResources,
    notes: s.notes, status: s.status,
  };
}

function ServiceForm({ initial, editingId, onCancel, onSaved }: {
  initial: UpsertPayload; editingId?: string; onCancel: () => void; onSaved: () => void;
}) {
  const [form, setForm] = useState<UpsertPayload>(initial);
  const [countriesText, setCountriesText] = useState(initial.countries.join(', '));
  const [tagsText, setTagsText] = useState(initial.tags.join(', '));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null); setSubmitting(true);
    try {
      const body: UpsertPayload = {
        ...form,
        countries: countriesText.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean),
        tags: tagsText.split(',').map((s) => s.trim()).filter(Boolean),
      };
      if (editingId) {
        await apiClient.put(`/admin/services-catalog/${editingId}`, body);
      } else {
        await apiClient.post('/admin/services-catalog', body);
      }
      onSaved();
    } catch (err) {
      setError(errorDetail(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      {error && <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <label className="space-y-1">
          <span className="text-xs font-medium uppercase text-muted-foreground">service_id</span>
          <Input value={form.serviceId} onChange={(e) => setForm({ ...form, serviceId: e.target.value })} placeholder="discovery-2week" required />
        </label>
        <label className="space-y-1">
          <span className="text-xs font-medium uppercase text-muted-foreground">name</span>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Discovery Sprint" required />
        </label>
        <label className="space-y-1">
          <span className="text-xs font-medium uppercase text-muted-foreground">category</span>
          <select className="w-full rounded border bg-background px-3 py-1.5 text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            <option value="consulting">consulting</option>
            <option value="implementation">implementation</option>
            <option value="training">training</option>
            <option value="support">support</option>
            <option value="resold">resold</option>
          </select>
        </label>
      </div>

      <label className="block space-y-1">
        <span className="text-xs font-medium uppercase text-muted-foreground">description</span>
        <textarea className="w-full rounded border bg-background px-3 py-2 text-sm" rows={2} value={form.description ?? ''} onChange={(e) => setForm({ ...form, description: e.target.value || null })} />
      </label>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <label className="space-y-1">
          <span className="text-xs font-medium uppercase text-muted-foreground">países (ISO codes · vacío = global)</span>
          <Input value={countriesText} onChange={(e) => setCountriesText(e.target.value)} placeholder="PR, CO, US" />
        </label>
        <label className="space-y-1">
          <span className="text-xs font-medium uppercase text-muted-foreground">tags</span>
          <Input value={tagsText} onChange={(e) => setTagsText(e.target.value)} placeholder="discovery, mvp" />
        </label>
      </div>

      <div className="space-y-3 rounded-lg border bg-muted/20 p-3">
        <label className="space-y-1">
          <span className="text-xs font-medium uppercase text-muted-foreground">pricing model</span>
          <select className="w-full rounded border bg-background px-3 py-1.5 text-sm" value={form.pricingModel} onChange={(e) => setForm({ ...form, pricingModel: e.target.value as PricingModel })}>
            <option value="hourly">hourly · T&M (profile rate × estimated hours)</option>
            <option value="fixed">fixed · project price</option>
            <option value="retainer">retainer · monthly bucket of hours</option>
            <option value="markup">markup · resold w/ % markup</option>
          </select>
        </label>

        {form.pricingModel === 'hourly' && (
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1">
              <span className="text-xs font-medium uppercase text-muted-foreground">default profile</span>
              <select className="w-full rounded border bg-background px-3 py-1.5 text-sm" value={form.defaultProfile ?? ''} onChange={(e) => setForm({ ...form, defaultProfile: e.target.value || null })}>
                <option value="">—</option>
                <option value="junior">Junior</option>
                <option value="mid">Mid</option>
                <option value="senior">Senior</option>
                <option value="expert">Expert</option>
              </select>
            </label>
            <label className="space-y-1">
              <span className="text-xs font-medium uppercase text-muted-foreground">estimated hours</span>
              <Input type="number" step="0.5" value={form.estimatedHours ?? ''} onChange={(e) => setForm({ ...form, estimatedHours: e.target.value ? parseFloat(e.target.value) : null })} />
            </label>
          </div>
        )}

        {form.pricingModel === 'fixed' && (
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1">
              <span className="text-xs font-medium uppercase text-muted-foreground">price USD (fijo)</span>
              <Input type="number" step="0.01" value={form.fixedPriceUsd ?? ''} onChange={(e) => setForm({ ...form, fixedPriceUsd: e.target.value ? parseFloat(e.target.value) : null })} />
            </label>
            <label className="space-y-1">
              <span className="text-xs font-medium uppercase text-muted-foreground">cost USD (interno)</span>
              <Input type="number" step="0.01" value={form.fixedCostUsd ?? ''} onChange={(e) => setForm({ ...form, fixedCostUsd: e.target.value ? parseFloat(e.target.value) : null })} />
            </label>
          </div>
        )}

        {form.pricingModel === 'retainer' && (
          <div className="grid grid-cols-3 gap-3">
            <label className="space-y-1">
              <span className="text-xs font-medium uppercase text-muted-foreground">hours/mes</span>
              <Input type="number" step="0.5" value={form.retainerHoursPerMonth ?? ''} onChange={(e) => setForm({ ...form, retainerHoursPerMonth: e.target.value ? parseFloat(e.target.value) : null })} />
            </label>
            <label className="space-y-1">
              <span className="text-xs font-medium uppercase text-muted-foreground">price MRR</span>
              <Input type="number" step="0.01" value={form.retainerPriceMrrUsd ?? ''} onChange={(e) => setForm({ ...form, retainerPriceMrrUsd: e.target.value ? parseFloat(e.target.value) : null })} />
            </label>
            <label className="space-y-1">
              <span className="text-xs font-medium uppercase text-muted-foreground">cost MRR</span>
              <Input type="number" step="0.01" value={form.retainerCostMrrUsd ?? ''} onChange={(e) => setForm({ ...form, retainerCostMrrUsd: e.target.value ? parseFloat(e.target.value) : null })} />
            </label>
          </div>
        )}

        {form.pricingModel === 'markup' && (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <label className="space-y-1">
              <span className="text-xs font-medium uppercase text-muted-foreground">upstream provider</span>
              <Input value={form.upstreamProvider ?? ''} onChange={(e) => setForm({ ...form, upstreamProvider: e.target.value || null })} placeholder="Hetzner" />
            </label>
            <label className="space-y-1">
              <span className="text-xs font-medium uppercase text-muted-foreground">cost/unit</span>
              <Input type="number" step="0.01" value={form.upstreamCostUnitUsd ?? ''} onChange={(e) => setForm({ ...form, upstreamCostUnitUsd: e.target.value ? parseFloat(e.target.value) : null })} />
            </label>
            <label className="space-y-1">
              <span className="text-xs font-medium uppercase text-muted-foreground">markup %</span>
              <Input type="number" step="0.1" value={form.markupPct ?? ''} onChange={(e) => setForm({ ...form, markupPct: e.target.value ? parseFloat(e.target.value) : null })} />
            </label>
            <label className="space-y-1">
              <span className="text-xs font-medium uppercase text-muted-foreground">unit label</span>
              <Input value={form.unitLabel ?? ''} onChange={(e) => setForm({ ...form, unitLabel: e.target.value || null })} placeholder="VPS/mes" />
            </label>
          </div>
        )}
      </div>

      <label className="block space-y-1">
        <span className="text-xs font-medium uppercase text-muted-foreground">notes</span>
        <textarea className="w-full rounded border bg-background px-3 py-2 text-sm" rows={2} value={form.notes ?? ''} onChange={(e) => setForm({ ...form, notes: e.target.value || null })} />
      </label>

      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" type="button" onClick={onCancel}>cancelar</Button>
        <Button size="sm" type="submit" disabled={submitting}>{submitting ? 'guardando…' : editingId ? 'guardar' : 'crear'}</Button>
      </div>
    </form>
  );
}
